import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Calendar, Pencil } from 'lucide-react';
import api from '../api';
import SEO from '../components/SEO';
import { formatInTz } from '../lib/datetime';
import { useAuth } from '../context/AuthContext';

export default function HelpArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setNotFound(false); setArticle(null);
    api.get(`/help/articles/${slug}`)
      .then(r => { if (!cancelled) setArticle(r.data); })
      .catch(err => { if (!cancelled && err?.response?.status === 404) setNotFound(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  if (notFound) return <Navigate to="/help" replace/>;

  return (
    <div className="bg-[#0a0a0b] text-zinc-100 min-h-screen">
      {article && (
        <SEO
          title={`${article.title} — Reachly Help`}
          description={article.excerpt || `Reachly help — ${article.title}`}
        />
      )}
      <section className="max-w-3xl mx-auto px-5 lg:px-8 pt-12 pb-24">
        <Link to="/help" className="text-sm text-zinc-500 hover:text-[#f5d67a] flex items-center gap-1 mb-6">
          <ArrowLeft size={14}/>All articles
        </Link>
        {loading && <div className="text-zinc-500">Loading…</div>}
        {article && (
          <>
            <div className="flex items-start justify-between gap-4">
              <div className="text-sm uppercase tracking-widest text-[#d4a94a]">{article.category}</div>
              {user?.is_admin && (
                <Link to={`/app/admin/help/${article.id}`} data-testid="help-article-edit">
                  <Button variant="outline" size="sm" className="border-[#3a2f18] text-[#f5d67a] hover:bg-[#1a1815]">
                    <Pencil size={12} className="mr-1"/>Edit
                  </Button>
                </Link>
              )}
            </div>
            <h1 className="mt-3 font-display text-4xl md:text-5xl leading-tight">{article.title}</h1>
            {article.excerpt && <p className="mt-4 text-zinc-400 text-lg leading-relaxed">{article.excerpt}</p>}
            {article.updated_at && (
              <div className="mt-6 flex items-center gap-1.5 text-xs text-zinc-500">
                <Calendar size={12}/>Updated {formatInTz(article.updated_at)}
              </div>
            )}
            <hr className="my-8 border-zinc-900"/>
            <article
              className="prose prose-invert prose-zinc max-w-none prose-headings:font-display prose-headings:text-zinc-100 prose-a:text-[#f5d67a] prose-strong:text-zinc-100 prose-code:text-[#f5d67a] prose-img:rounded-lg prose-img:border prose-img:border-zinc-900"
              // Content is written by admins in a rich-text editor and stored
              // as HTML. Since it's admin-authored (require_admin gate on
              // write), dangerouslySetInnerHTML is safe here.
              dangerouslySetInnerHTML={{ __html: article.body_html || '<em class="text-zinc-500">This article is empty.</em>' }}
              data-testid="help-article-body"
            />
            <Card className="mt-12 bg-gradient-to-b from-[#1a1613] to-[#141110] border-[#3a2f18] p-6">
              <div className="font-display text-xl">Still stuck?</div>
              <p className="mt-2 text-sm text-zinc-300">Email <a href="mailto:hello@ireachly.com" className="text-[#f5d67a] hover:underline">hello@ireachly.com</a> and we'll walk you through it.</p>
              <div className="mt-4 flex gap-2">
                <Link to="/help"><Button variant="outline" className="border-zinc-800 text-zinc-200 hover:bg-zinc-900">More articles</Button></Link>
                <Link to="/signup"><Button className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium">Try Reachly free</Button></Link>
              </div>
            </Card>
          </>
        )}
      </section>
    </div>
  );
}
